import type {
  SearchFiltersSchema,
  SimpleFilterEntitySchema,
} from "@/lib/api/model";

export type ClearanceLevel = NonNullable<
  SearchFiltersSchema["clearance_levels"]
>[number];
export type ClearancePolygraph = NonNullable<
  SearchFiltersSchema["clearance_polygraphs"]
>[number];

// Ladder order (low → high). DoD and DoE are separate ladders; the backend
// never cross-matches them.
export const CLEARANCE_LEVELS: { value: ClearanceLevel; label: string }[] = [
  { value: "public_trust", label: "Public Trust" },
  { value: "confidential", label: "Confidential" },
  { value: "secret", label: "Secret" },
  { value: "top_secret", label: "Top Secret" },
  { value: "ts_sci", label: "TS/SCI" },
  { value: "doe_l", label: "DoE L" },
  { value: "doe_q", label: "DoE Q" },
];

export const CLEARANCE_POLYGRAPHS: {
  value: ClearancePolygraph;
  label: string;
}[] = [
  { value: "ci", label: "CI Poly" },
  { value: "full_scope", label: "Full Scope Poly" },
  { value: "lifestyle", label: "Lifestyle Poly" },
];

/** One grouped option list for the single Security Clearance picker. */
export const CLEARANCE_OPTIONS: { value: string; label: string; category: string }[] = [
  ...CLEARANCE_LEVELS.map((l) => ({ ...l, category: "Level" })),
  ...CLEARANCE_POLYGRAPHS.map((p) => ({ ...p, category: "Polygraph" })),
];

export const CLEARANCE_LEVEL_LABELS: Record<string, string> =
  Object.fromEntries(CLEARANCE_LEVELS.map((l) => [l.value, l.label]));

export const CLEARANCE_POLYGRAPH_LABELS: Record<string, string> =
  Object.fromEntries(CLEARANCE_POLYGRAPHS.map((p) => [p.value, p.label]));

// Highest level on each ladder: nothing above them, so strict is a no-op.
const TOP_OF_LADDER = new Set<string>(["ts_sci", "doe_q"]);

// Old `security_clearance` highlight ids. The backend still applies them for
// saved searches but no longer lists them in the highlight catalog; the
// panel shows them in the Clearances section instead of Highlights.
export const LEGACY_CLEARANCE_HIGHLIGHT_IDS: ReadonlySet<string> = new Set([
  "secret_clearance",
  "top_secret_clearance",
  "top_secret_sci_clearance",
  "ts_full_scope_poly_clearance",
  "ts_lifestyle_poly_clearance",
  "ts_ci_poly_clearance",
  "doe_q_clearance",
]);

const CLEARANCE_LEVEL_VALUES = new Set<string>(
  CLEARANCE_LEVELS.map((l) => l.value),
);
const CLEARANCE_POLYGRAPH_VALUES = new Set<string>(
  CLEARANCE_POLYGRAPHS.map((p) => p.value),
);

export function isClearanceLevel(value: string): value is ClearanceLevel {
  return CLEARANCE_LEVEL_VALUES.has(value);
}

export function isClearancePolygraph(
  value: string,
): value is ClearancePolygraph {
  return CLEARANCE_POLYGRAPH_VALUES.has(value);
}

export function isLegacyClearanceHighlight(id: string): boolean {
  return LEGACY_CLEARANCE_HIGHLIGHT_IDS.has(id);
}

export function splitLegacyClearanceHighlights(
  items: SimpleFilterEntitySchema[] | null | undefined,
): {
  highlights: SimpleFilterEntitySchema[];
  legacyClearances: SimpleFilterEntitySchema[];
} {
  const highlights: SimpleFilterEntitySchema[] = [];
  const legacyClearances: SimpleFilterEntitySchema[] = [];
  for (const item of items ?? []) {
    (isLegacyClearanceHighlight(item.text) ? legacyClearances : highlights).push(
      item,
    );
  }
  return { highlights, legacyClearances };
}

/**
 * Selected highlights that are still in the catalog. Legacy clearance ids are
 * kept even when the catalog is empty: the backend still applies them.
 */
export function pruneUnknownHighlights(
  selected: SimpleFilterEntitySchema[],
  catalogTypes: Iterable<string>,
): SimpleFilterEntitySchema[] {
  const allowed = new Set(catalogTypes);
  return selected.filter(
    (item) => allowed.has(item.text) || isLegacyClearanceHighlight(item.text),
  );
}

/** Chip label for a selected level, e.g. "Secret or higher" or "Secret only". */
export function clearanceLevelChipLabel(
  level: string,
  strict: boolean | null | undefined,
): string {
  const label = CLEARANCE_LEVEL_LABELS[level] ?? level;
  if (TOP_OF_LADDER.has(level)) return label;
  return strict ? `${label} only` : `${label} or higher`;
}

/** True when "or higher" vs "exact" changes the result for these levels. */
export function levelsHaveHigherLevels(levels: readonly string[]): boolean {
  return levels.some((level) => !TOP_OF_LADDER.has(level));
}

function joinOr(items: string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} or ${items.at(-1)}`;
}

/**
 * Plain-language reading of a level + polygraph selection, e.g.
 * "Secret or higher / TS/SCI · with CI Poly or Full Scope Poly". Null unless
 * both are set, since that's when the AND between them needs spelling out.
 */
export function clearanceSummary(
  levels: readonly string[],
  polygraphs: readonly string[],
  strict: boolean | null | undefined,
): string | null {
  if (levels.length === 0 || polygraphs.length === 0) return null;
  const levelText = levels.map((l) => clearanceLevelChipLabel(l, strict)).join(" / ");
  const polyText = joinOr(polygraphs.map((p) => CLEARANCE_POLYGRAPH_LABELS[p] ?? p));
  return `${levelText} · with ${polyText}`;
}

/** Active clearance inputs; `clearance_strict` modifies levels, so it isn't counted. */
export function countClearanceFilters(
  filters: Pick<
    SearchFiltersSchema,
    "clearance_levels" | "clearance_polygraphs" | "highlight_types"
  >,
): number {
  return (
    (filters.clearance_levels?.length ?? 0) +
    (filters.clearance_polygraphs?.length ?? 0) +
    splitLegacyClearanceHighlights(filters.highlight_types).legacyClearances
      .length
  );
}
